import {Component, computed, inject, input, InputSignal, Signal} from '@angular/core';
import {FrameAssemblyService} from "../service/frame-assembly.service";
import {AssemblyFrameInput} from "../models/assembly-frame-input";
import {AssemblySpacingInput} from "../models/spacing-input";
import {fallbackValidation} from "../../shared/util/fallbackValidation";
import {toNumber} from "../../shared/util/toNumber";
import {inRange} from "../../shared/util/inRange";
import {of, tap} from "rxjs";
import {map} from "rxjs/operators";
import {FormsModule} from "@angular/forms";
import {LamelareaImpl} from "../../shared/product-card/product-card/lamelarea.class";
import {InputComponent} from "../../shared/form-elements/input/input/input.component";
import {OrderByPipe} from "../../shared/pipe/orderBy.pipe";
import {CardElementComponent} from "../../shared/card-wrap/card-element/card-element/card-element.component";
import {CardWrapComponent} from "../../shared/card-wrap/card-wrap/card-wrap.component";
import {TranslatePipe} from "@ngx-translate/core";
import {LamelareaStore} from "../../shared/product-card/product-card/lamelarea.store";

@Component({
    selector: 'app-assembly-spreadsheet-configurator',
    standalone: true,
    imports: [
        FormsModule,
        InputComponent,
        OrderByPipe,
        CardElementComponent,
        CardWrapComponent,
        TranslatePipe
    ],
    templateUrl: './assembly-spreadsheet-configurator.component.html',
    styleUrl: './assembly-spreadsheet-configurator.component.scss'
})
export class AssemblySpreadsheetConfiguratorComponent {

    private lamelAreaStore = inject(LamelareaStore)
    private frameAssemblyService = inject(FrameAssemblyService);

    public lamellaArea: InputSignal<LamelareaImpl> = input();


    /**
     * Signal to provide the orientation of the assembly
     * @protected
     */
    public orientation: Signal<'horizontal'|'vertical'> = computed(()=> this.lamellaArea()?.orientation ?? 'horizontal');

    /**
     * Array of frame and spacing inputs for components, provided by the frameAssemblyStore store
     * @protected
     */
    protected assemblyArray: Signal<Array<AssemblySpacingInput|AssemblyFrameInput>> = computed(()=> this.lamellaArea()?.assemblyArray ?? [] );


    /**
     * filtered spacings from assemblyArray
     * @protected
     */
    protected spacingComponents: Signal<Array<any>> = computed( () => {
        return this.assemblyArray()
            ?.filter( this.frameAssemblyService.isSpacingInput )
            .filter((spacing: any)=>!spacing.overflowSpace) //remove overflow spans
            ?? [];
    });

    /**
     * filtered overflow frames from assemblyArray
     * @protected
     */
    protected overFlowComponents: Signal<Array<any>> = computed( () => {
        return this.assemblyArray()
                ?.filter( this.frameAssemblyService.isSpacingInput )
                .filter( ( spacing: any )=> spacing.overflowSpace )
                ?? [];
    })


    public handleSpanChange(item: any, element: HTMLInputElement): void {
        const memoizedValueTransformer = fallbackValidation<unknown, number>(
            item.maxSpan,
            (error, fallbackValue) =>{ element.value = fallbackValue.toString() }, //on error sets element value to fallback value
            toNumber,
            inRange(
                Math.max(item.minSpan,item.span-this.frameAssemblyService.maxGivableSpanLamellaArea(item.index, this.assemblyArray())), //min [ item.minSpan : item.span ]
                item.span <= toNumber(element.value)  //is modified span growing?
                    ? item.span + this.frameAssemblyService.maxLoanableSpanLamellaArea(item.index,this.assemblyArray()) //max [ item.span : item.span + maxLoanableSpan ]
                    : item.span + this.frameAssemblyService.maxGivableSpanLamellaArea(item.index,this.assemblyArray()) //max [ item.span : item.span + maxGivableSpan ]
            )
        )

        of(element.value).pipe(
            map(memoizedValueTransformer),
            tap((value)=> element.value = value.toString())
        ).subscribe((val: number) => this.lamelAreaStore.setSpanValue(this.lamellaArea().id, item.index, val))
    }

    public handleFrameReinforcementToggle(index: number){
        //this.frameAssemblyStore.reinforceFrames(this.lamellaArea().id, index);
        //this.assemblyConfiguration().mode.set('manual');
    }

    public handleOverflowChange(item: any, value: string|number|null, element: HTMLInputElement): void {

        const validation = fallbackValidation(
            item.span, //fallback value
            (error, fallbackValue) =>{ element.value = fallbackValue.toString() }, //on error sets element value to fallback value
            toNumber, //transformer
            inRange(item.minSpan, item.maxSpan) //transformer
        );

        of(value)
            .pipe(
                map(validation),
                tap((value)=>element.value = value)
                //tap(()=> this.assemblyConfiguration().mode.set('manual'))
            )
            .subscribe((val: number) =>
                this.lamelAreaStore.setOverflowSpanValue(this.lamellaArea().id, item.index, val)
            );
    }

    public splitSpan(index: number){
        this.lamelAreaStore.splitFrameSpan(this.lamellaArea().id, index);

    }

    public conjoinSpan(index: number) {
        this.lamelAreaStore.conjoinFrameSpans(this.lamellaArea().id, index);
    }


}
